import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfig } from 'src/app/auth/helpers/app-config';

@Injectable({
  providedIn: 'root'
})
export class RouterInfoService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // initial 3s
  private maxReconnectDelay = 30000;

  private wifiUpdatesSubject = new Subject<any>();
  private connectedDevicesSubject = new Subject<any[]>();
  private connectionStatusSubject = new Subject<boolean>();
  private messageSubject = new Subject<any>();

  constructor(private http: HttpClient, private config: AppConfig) { }

  // HTTP calls
  getConnectedDevices(): Observable<any[]> {
    return this.http.get<any[]>(`${this.config.api}/client/connecteddevices/`);
  }

  getTR069Stats(): Observable<any> {
    return this.http.get<any>(`${this.config.api}/client/tr069-stats/`);
  }

  getWifiSettings(): Observable<any> {
    return this.http.get<any>(`${this.config.api}/client/wifi-settings/`);
  }

  updateWifiSettings(data: { ssid: string; password: string }): Observable<any> {
    return this.http.post<any>(`${this.config.api}/client/update-wifi-settings/`, data);
  }

  getUserDetails(): Observable<any> {
    return this.http.get(`${this.config.api}/client/dashboard/`);
  }

  getDataConsumption(): Observable<any> {
    return this.http.get(`${this.config.api}/client/data-consumption/`);
  }

  // Public Observables
  getWifiUpdates(): Observable<any> {
    return this.wifiUpdatesSubject.asObservable();
  }

  fetchWifiData(deviceId: string, sessionId: string, endpoint: string): Observable<any> {
    const url = `${this.config.tr069UrlApi}/admin/cwmp/config/${endpoint}?devID=${deviceId}&session=${sessionId}`;

    return this.http.get<any>(url).pipe(
      catchError(error => {
        console.error(`Error fetching ${endpoint}:`, error);
        return throwError(() => error); // <-- use `throwError` properly
      })
    );
  }

  getWifiPorts(): Observable<any> {
    return this.http.get<any>(`${this.config.api}/client/wifi/ports/`);
  }

  setWifi(data: {
    wifi_port: string;
    ssid: string;
    password: string;
    authentication_mode?: string;
    dhcp?: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.config.api}/client/wifi/set/`, data);
  }

  getConnectedDevicesUpdates(): Observable<any[]> {
    return this.connectedDevicesSubject.asObservable();
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  getMessages(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connectionStatusSubject.next(false);
    }
  }

 updatePassword(id: string, password: string, session: string, ssidIndex: number): Observable<any> {
  const baseUrl = `${this.config.tr069UrlApi}/admin/cwmp/config/change-password`;
  const queryParams = `?devID=${id}&password=${password}&session=${session}&ssidCount=${ssidIndex}`;
  const url = `${baseUrl}${queryParams}`;

  return this.http.post(url, {});
}


   saveSsidConfig(formData: any): Observable<any> {
    const url = `${this.config.tr069UrlApi}/admin/cwmp/config/save-ssid`;
    return this.http.post(url, formData);
  }

  // Connect with token, deviceId and sessionId
  connect(deviceId: string, sessionId: string): Observable<boolean> {
    const token = localStorage.getItem('tr069_access_token');
    if (!token) {
      console.error('No access token found');
      this.connectionStatusSubject.error('No access token found');
      return this.connectionStatusSubject.asObservable();
    }

    const wsUrl = `${this.config.websocketUrl}?token=${token}&deviceid=${deviceId}&session=${sessionId}`;
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      console.log('✅ WebSocket connected');
      this.reconnectAttempts = 0;
      this.connectionStatusSubject.next(true);
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const payload = data?.data;

        this.messageSubject.next(payload);
        console.log('WebSocket message received:', payload, typeof data);

        if (payload?.wanconfigs || payload?.associatedDevices || payload?.apwm) {
          // Emit WiFi update if apwm exists
          if (payload?.apwm || payload?.wanconfigs) {
            const wlan = Object.values(payload?.wanconfigs)[0];
            console.log('wlan data', wlan);
            // const wifiUpdate = {
            //   ssid: wlan?.SSID || '',
            //   password: wlan?.KeyPassphrase || ''
            // };
            this.wifiUpdatesSubject.next(payload?.wanconfigs);
          }

          // Emit connected devices update
          if (payload?.associatedDevices) {
            const devices = Object.values(payload?.associatedDevices);
            this.connectedDevicesSubject.next(devices);
          }
        }
      } catch (err) {
        console.error('❌ Error parsing WebSocket message:', err);
      }
    };

    this.socket.onerror = (error: Event) => {
      console.error('❌ WebSocket error:', error);
      this.connectionStatusSubject.error('WebSocket error');
    };

    this.socket.onclose = () => {
      console.warn('⚠️ WebSocket closed');
      this.connectionStatusSubject.next(false);
      this.reconnect(deviceId, sessionId);
    };

    return this.connectionStatusSubject.asObservable();
  }




  private reconnect(deviceId: string, sessionId: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnect attempts reached');
      this.connectionStatusSubject.complete();
      return;
    }

    const delay = Math.min(this.reconnectDelay * (2 ** this.reconnectAttempts), this.maxReconnectDelay);
    console.log(`🔄 Reconnecting in ${delay / 1000} seconds...`);

    timer(delay).subscribe(() => {
      this.reconnectAttempts++;
      this.connect(deviceId, sessionId);
    });
  }


}
